import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import './styles/globals.css';
import { dairyMockApi } from './services/dairyMock';
import { coaMockApi } from './services/coaMock';
import { movementTypeMockApi } from './services/movementTypeMock';
import { purchaseMasterMockApi } from '../modules/purchase/services/vendorService';
import { prService } from '../modules/purchase/services/prService';
import { rfqService } from '../modules/purchase/services/rfqService';
import { poService } from '../modules/purchase/services/poService';
import { grnService } from '../modules/purchase/services/grnService';
import { invoiceService, paymentService } from '../modules/purchase/services/invoiceService';
import { api, apiText, qs } from './services/http';


if (!window.electronAPI) {
  console.warn('Browser mode: screens talk to the plant server.');

  const unavailable = async () => ({
    success: false,
    error: 'This action is not available in the plant system yet. Nothing was saved.',
  });

  window.electronAPI = {
    auth: {
      login: async (username: string, password: string) => {
        const res = await api<any>('/api/auth/login', {
          method: 'POST',
          body: JSON.stringify({ username, password }),
        });
        if (!res.success) return { success: false, error: res.error };
        const { token, ...user } = res.data || {};
        if (token) localStorage.setItem('dms_token', token);
        return { success: true, data: user };
      },
      logout: async () => {
        localStorage.removeItem('dms_token');
        return { success: true };
      },
      getCurrentUser: async () => {
        const token = localStorage.getItem('dms_token');
        if (!token) return { success: false, error: 'No session' };
        const res = await api<any>('/api/auth/me');
        if (!res.success) return res;
        return { success: true, data: res.data };
      },
      changePassword: async (_userId: string, oldPassword: string, newPassword: string) =>
        api('/api/account/password', {
          method: 'POST',
          body: JSON.stringify({ current_password: oldPassword, new_password: newPassword }),
        }),
    },
    dashboard: {
      getStats: async () => api('/api/dashboard/stats'),
      getRecentTransactions: async () => api('/api/dashboard/recent'),
    },
    finance: {
      getAccounts: async () => coaMockApi.getChartOfAccounts(),
      getAccountTypes: async () => coaMockApi.getAccountTypes(),
      getChartOfAccounts: async (tenantId: string) => {
        const res = await coaMockApi.getChartOfAccounts(tenantId);
        if (!res.success || !res.data) return res;
        // Map to UI-friendly shape used by COA page + legacy callers
        return {
          success: true,
          data: res.data.map((a: any) => ({
            ...a,
            code: a.account_code,
            name: a.account_name,
            type: a.account_type_name,
            balance: a.current_balance,
            parent_account: a.parent_id,
          })),
        };
      },
      getLedgerAccounts: async (_tid: string, filters?: any) => coaMockApi.getLedgerAccounts(filters),
      getParentGroups: async (_tid: string, typeId: string) => coaMockApi.getParentGroups(typeId),
      createAccount: async (tenantId: string, data: any) => coaMockApi.createAccount(tenantId, data),
      updateAccount: async (tenantId: string, id: string, data: any) =>
        coaMockApi.updateAccount(tenantId, id, data),
      deleteAccount: async (tenantId: string, id: string) => coaMockApi.deleteAccount(tenantId, id),
      getJournalEntries: async () => {
        const res = await coaMockApi.getVouchers();
        if (!res.success || !res.data) return res;
        return {
          success: true,
          data: res.data.map((v: any) => ({
            id: v.id,
            entry_number: v.voucher_no,
            voucher_no: v.voucher_no,
            voucher_type: v.voucher_type,
            date: v.voucher_date,
            reference: v.voucher_type,
            narration: v.narration,
            total_debit: v.total_debit,
            total_credit: v.total_credit,
            status: v.status === 'approved' ? 'posted' : v.status,
            raw_status: v.status,
            lines: v.lines.map((l: any) => ({
              account_code: l.account_code,
              account_name: l.account_name,
              account_id: l.account_id,
              debit: l.entry_type === 'debit' ? l.amount : 0,
              credit: l.entry_type === 'credit' ? l.amount : 0,
              entry_type: l.entry_type,
              amount: l.amount,
            })),
            created_by: v.created_by,
            approved_by: v.approved_by,
          })),
        };
      },
      getVouchers: async (tenantId?: string) => coaMockApi.getVouchers(tenantId),
      /** Reference docs from sales/purchase modules for voucher linking */
      getDocumentRefs: async (_tid: string, kind: 'sales' | 'purchase' | 'all' = 'all') => {
        const mapRow = (row: any, docKind: 'sales' | 'purchase') => ({
          id: row.id,
          kind: docKind,
          number: row.invoice_number || row.invoice_no,
          date: row.date || row.invoice_date,
          party: row.customer_name || row.vendor_name || row.party_name || '',
          amount: Number(row.total_amount ?? row.grand_total ?? row.total ?? row.amount) || 0,
          status: row.status,
        });
        const salesRes = kind === 'purchase' ? null : await api<any[]>('/api/sales/invoices');
        const purchaseRes = kind === 'sales' ? null : await api<any[]>('/api/purchase/invoices');
        if (salesRes && !salesRes.success) return salesRes;
        if (purchaseRes && !purchaseRes.success) return purchaseRes;
        const sales = (salesRes?.data || []).map((row) => mapRow(row, 'sales'));
        const purchase = (purchaseRes?.data || []).map((row) => mapRow(row, 'purchase'));
        if (kind === 'sales') return { success: true, data: sales };
        if (kind === 'purchase') return { success: true, data: purchase };
        return { success: true, data: [...sales, ...purchase] };
      },
      createVoucher: async (tenantId: string, data: any, action?: string) =>
        coaMockApi.saveVoucher(
          tenantId,
          data,
          (action as any) || 'draft',
          data.created_by || 'User'
        ),
      submitVoucher: async (id: string) => coaMockApi.submitVoucher(id),
      approveAndPostVoucher: async (id: string, approvedBy?: string) =>
        coaMockApi.approveAndPostVoucher(id, approvedBy),
      rejectVoucher: async (id: string) => coaMockApi.rejectVoucher(id),
      createJournalEntry: async (tid: string, data: any) => {
        // legacy: map debit/credit lines → voucher lines
        const lines = (data.lines || []).flatMap((l: any) => {
          const out: any[] = [];
          if (l.debit > 0)
            out.push({ account_id: l.account_id, entry_type: 'debit', amount: l.debit });
          if (l.credit > 0)
            out.push({ account_id: l.account_id, entry_type: 'credit', amount: l.credit });
          return out;
        });
        return coaMockApi.saveVoucher(
          tid,
          {
            voucher_type: data.voucher_type || 'journal',
            voucher_date: data.date || new Date().toISOString().slice(0, 10),
            narration: data.narration || '',
            created_by: data.created_by,
            lines,
          },
          data.action || 'draft',
          data.created_by || 'User'
        );
      },
      getTrialBalance: async (_tid: string, asOn?: string) => coaMockApi.getTrialBalance(asOn),
      getBankReconciliation: async (_tid?: string, accountId?: string) =>
        api(`/api/finance/bank-reconciliation${qs({ accountId })}`),
      saveBankReconciliation: async (lines: any[]) =>
        api('/api/finance/bank-reconciliation', { method: 'POST', body: JSON.stringify({ lines }) }),
      getCreditLimits: async () => api('/api/sales/credit-limits'),
      getBalanceSheet: async () => {
        const res = await coaMockApi.getChartOfAccounts();
        if (!res.success || !Array.isArray(res.data)) return res;
        const rows = (type: string) => res.data
          .filter((account: any) => !account.is_group && account.account_type_name === type)
          .map((account: any) => ({ name: account.account_name, amount: Number(account.current_balance) || 0 }));
        return {
          success: true,
          data: {
            assets: { current: rows('Asset'), fixed: [] },
            liabilities: { current: rows('Liability'), longTerm: [] },
            equity: rows('Equity'),
          },
        };
      },
      getProfitAndLoss: async () => coaMockApi.getProfitAndLoss(),
      getAccountLedger: async (_tid: string, accountId: string, from?: string, to?: string) =>
        coaMockApi.getLedger(accountId, from, to),
      ...new Proxy({}, { get: () => unavailable })
    },
    inventory: {
      getItems: async () => api('/api/inventory/materials'),
      createItem: async (_tenantId: string, data: any) =>
        api('/api/inventory/materials', {
          method: 'POST',
          body: JSON.stringify({ ...data, item_name: data.name || data.item_name, item_type: data.type }),
        }),
      updateItem: async (_tenantId: string, id: string, data: any) =>
        api(`/api/inventory/materials/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ ...data, item_name: data.name || data.item_name }),
        }),
      deleteItem: async (_tenantId: string, id: string) =>
        api(`/api/inventory/materials/${id}`, { method: 'DELETE' }),
      getLowStockAlerts: async () => api('/api/inventory/low-stock'),
      getWarehouses: async () => api('/api/inventory/warehouses'),
      createWarehouse: async (data: any) =>
        api('/api/inventory/warehouses', { method: 'POST', body: JSON.stringify(data) }),
      getStockMovements: async () => api('/api/inventory/ledger'),
      getStockValuation: async () => api('/api/inventory/valuation'),
      getGatePasses: async () => api('/api/records?module=inventory&kind=gate_pass'),
      createGatePass: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'inventory', kind: 'gate_pass', ...data }) }),
      ...new Proxy({}, { get: () => unavailable })
    },
    sales: {
      getCustomers: async () => api('/api/sales/customers'),
      createCustomer: async (_tenantId: string, data: any) =>
        api('/api/sales/customers', { method: 'POST', body: JSON.stringify(data) }),
      updateCustomer: async (_tenantId: string, id: string, data: any) =>
        api(`/api/sales/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      deleteCustomer: async (_tenantId: string, id: string) =>
        api(`/api/sales/customers/${id}`, { method: 'DELETE' }),
      getSalesOrders: async () => api('/api/sales/orders'),
      createSalesOrder: async (_tid: string, data: any) =>
        api('/api/sales/orders', { method: 'POST', body: JSON.stringify(data) }),
      createDelivery: async (orderId: string) =>
        api('/api/sales/deliveries', { method: 'POST', body: JSON.stringify({ order_id: orderId }) }),
      invoiceSalesOrder: async (orderId: string) =>
        api(`/api/sales/orders/${orderId}/invoice`, { method: 'POST' }),
      createCustomerReceipt: async (_tid: string, data: any) =>
        api('/api/sales/receipts', { method: 'POST', body: JSON.stringify(data) }),
      getSalesInvoices: async () => api('/api/sales/invoices'),
      printInvoice: async (id: string) => {
        const res = await apiText(`/api/sales/invoices/${id}/document`);
        if (!res.success || !res.data) return res;
        const preview = window.open('', '_blank');
        if (!preview) {
          return { success: false, error: 'The invoice could not open. Allow pop-up windows and try again.' };
        }
        preview.document.open();
        preview.document.write(res.data);
        preview.document.close();
        return { success: true };
      },
      createSalesInvoice: async () => ({ success: false, error: 'Create a sales order, then invoice that order.' }),
      getCustomerReceipts: async () => api('/api/sales/receipts'),
      getAgingReport: async () => api('/api/sales/aging'),
      getSalesReport: async (_tenantId: string, from?: string, to?: string) =>
        api(`/api/sales/report${qs({ from, to })}`),
      getPOSProducts: async () => {
        const res = await api<any[]>('/api/inventory/materials');
        if (!res.success) return res;
        return {
          success: true,
          data: (res.data || []).filter((item) => item.is_active !== false).map((item) => ({
            id: item.id,
            sku: item.item_code,
            name: item.item_name,
            barcode: item.item_code,
            price: Number(item.sales_rate || item.unit_price) || 0,
            gst_rate: Number(item.gst_rate) || 0,
            hsn_code: item.hsn_code,
            stock: Number(item.current_stock) || 0,
            category: item.category || item.item_type,
          })),
        };
      },
      createPOSTransaction: async (_tid: string, data: any) =>
        api('/api/sales/orders', { method: 'POST', body: JSON.stringify(data) }),
      ...new Proxy({}, { get: () => unavailable })
    },
    purchase: {
      getPurchaseOrgs: async () => purchaseMasterMockApi.getPurchaseOrgs(),
      getPurchaseGroups: async () => purchaseMasterMockApi.getPurchaseGroups(),
      getPurchaseOrgByCode: async (code: string) => purchaseMasterMockApi.getPurchaseOrgByCode(code),
      getPurchaseGroupByCode: async (code: string) =>
        purchaseMasterMockApi.getPurchaseGroupByCode(code),
      createPurchaseOrg: async (data: any) => purchaseMasterMockApi.createPurchaseOrg(data),
      createPurchaseGroup: async (data: any) => purchaseMasterMockApi.createPurchaseGroup(data),
      peekNextCodes: async () => purchaseMasterMockApi.peekNextCodes(),
      getVendors: async (_tid?: string, filters?: any) => purchaseMasterMockApi.getVendors(filters),
      getVendorById: async (id: string) => purchaseMasterMockApi.getVendorById(id),
      getPayableVendors: async () => purchaseMasterMockApi.getPayableVendors(),
      createVendor: async (tenantId: string, data: any) =>
        purchaseMasterMockApi.createVendor(tenantId, data),
      updateVendor: async (_tenantId: string, id: string, data: any) =>
        purchaseMasterMockApi.updateVendor(id, data),
      deactivateVendor: async (id: string) => purchaseMasterMockApi.deactivateVendor(id),
      deleteVendor: async (_tenantId: string, id: string) =>
        purchaseMasterMockApi.deactivateVendor(id),
      getMaterials: async (filters?: any) => purchaseMasterMockApi.getMaterials(filters),
      createMaterial: async (data: any) => purchaseMasterMockApi.createMaterial(data),
      // PR
      getPurchaseRequisitions: async (filters?: any) => prService.getAll(filters),
      getPurchaseRequisition: async (id: string) => prService.getById(id),
      createPurchaseRequisition: async (data: any, status?: any) => prService.create(data, status),
      submitPurchaseRequisition: async (id: string) => prService.submit(id),
      approvePurchaseRequisition: async (id: string, by?: string) => prService.approve(id, by || 'Admin'),
      rejectPurchaseRequisition: async (id: string, reason?: string) => prService.reject(id, reason || ''),
      // RFQ
      getRFQs: async (filters?: any) => rfqService.getAll(filters),
      getRFQ: async (id: string) => rfqService.getById(id),
      createRFQ: async (data: any, status?: any) => rfqService.create(data, status),
      addRFQQuotation: async (rfqId: string, quote: any) => rfqService.addQuotation(rfqId, quote),
      selectRFQVendor: async (rfqId: string, vendorId: string) => rfqService.selectVendor(rfqId, vendorId),
      getPurchaseOrders: async () => poService.getAll(),
      getPurchaseOrderItems: async (_tid: string, poId: string) => {
        const res = await poService.getById(String(poId));
        if (!res.success) return res;
        return { success: true, data: res.data?.items || [] };
      },
      getPurchaseInvoices: async () => invoiceService.getAll(),
      getGoodsReceiptNotes: async () => grnService.getAll(),
      getVendorPayments: async () => paymentService.getAll(),
      getPurchaseReport: async (_tenantId: string, from?: string, to?: string) =>
        api(`/api/purchase/report${qs({ from, to })}`),
      ...new Proxy({}, { get: () => unavailable })
    },
    gst: {
      getGSTLiabilitySummary: async (_tenantId: string, from?: string, to?: string) =>
        api(`/api/gst/liability${qs({ from, to })}`),
      getGSTR1Data: async (_tid: string, month?: string) => api(`/api/gst/gstr1${qs({ month })}`),
      getGSTR1B2BDetails: async (_tid: string, month?: string) => api(`/api/gst/gstr2b${qs({ month })}`),
      performITCReconciliation: async (_tid: string, month?: string) => api(`/api/gst/itc${qs({ month })}`),
      getHSNWiseSummary: async (_tid: string, from?: string, to?: string) => api(`/api/gst/hsn${qs({ from, to })}`),
      getTdsSections: async () => api('/api/gst/tds/sections'),
      getTdsTransactions: async () => api('/api/gst/tds/transactions'),
      getTdsSummary: async () => api('/api/gst/tds/summary'),
      getEInvoices: async () => api('/api/gst/einvoices'),
      generateIRN: async (_tid: string, invoiceId: string) =>
        api('/api/gst/einvoices', { method: 'POST', body: JSON.stringify({ invoice_id: invoiceId }) }),
      cancelIRN: async () => ({ success: false, error: 'The government e-invoice portal is not connected. The plant copy stays in the database.' }),
      getEWayBills: async () => {
        const res = await api<any[]>('/api/gst/eway-bills');
        if (!res.success) return res;
        return { success: true, data: { bills: res.data || [], consolidated: [] } };
      },
      generateEWayBill: async (_tid: string, data: any) =>
        api('/api/gst/eway-bills', { method: 'POST', body: JSON.stringify(data) }),
      updateEWayBill: async (_tid: string, id: string, data: any) =>
        api(`/api/gst/eway-bills/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      cancelEWayBill: async (_tid: string, id: string) =>
        api(`/api/gst/eway-bills/${id}/cancel`, { method: 'POST' }),
      extendEWayBill: async () => ({
        success: false,
        error: 'Validity is set on the GST portal after you upload this JSON. This plant copy does not extend a government e-Way Bill.',
      }),
      ...new Proxy({}, { get: () => unavailable })
    },
    manufacturing: {
      getBOMs: async () => api('/api/records?module=manufacturing&kind=bom'),
      createBOM: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'bom', ...data }) }),
      getBOMComponents: async (_tid: string, bomId: string) => api(`/api/records?module=manufacturing&kind=bom_component_${bomId}`),
      getBOMOperations: async (_tid: string, bomId: string) => api(`/api/records?module=manufacturing&kind=bom_operation_${bomId}`),
      getProductionOrders: async () => api('/api/records?module=manufacturing&kind=production_order'),
      createProductionOrder: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'production_order', ...data }) }),
      getProductionOrderMaterials: async () => ({ success: true, data: [] }),
      getWorkCenters: async () => api('/api/records?module=manufacturing&kind=work_center'),
      createWorkCenter: async (data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'work_center', ...data }) }),
      getOEEMetrics: async () => ({ success: true, data: { overall: { availability: 0, performance: 0, quality: 0, oee: 0 }, machines: [], downtimeReasons: [], trends: { dates: [], oee: [] } } }),
      getQCInspections: async () => api('/api/records?module=manufacturing&kind=qc'),
      createQCInspection: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'qc', ...data }) }),
      getRawMaterialInspections: async (filters?: { from?: string; to?: string; shifts?: string }) =>
        api(`/api/qc/tanker-reports${qs(filters || {})}`),
      createRawMaterialInspection: async (data: any) =>
        api('/api/qc/tanker-reports', { method: 'POST', body: JSON.stringify(data) }),
      updateRawMaterialInspection: async (id: string, data: any) =>
        api(`/api/qc/tanker-reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      getProductInspections: async (filters?: { from?: string; to?: string; shifts?: string }) =>
        api(`/api/qc/product-reports${qs(filters || {})}`),
      createProductInspection: async (data: any) =>
        api('/api/qc/product-reports', { method: 'POST', body: JSON.stringify(data) }),
      updateProductInspection: async (id: string, data: any) =>
        api(`/api/qc/product-reports/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      getJobWorkChallans: async () => {
        const res = await api<any[]>('/api/records?module=manufacturing&kind=job_work');
        if (!res.success) return res;
        return { success: true, data: { challans: res.data || [], itc04Summary: { quarter: '', total_outward: 0, total_inward: 0, pending_return: 0, total_value_outward: 0, total_value_inward: 0, filing_status: 'not_filed', due_date: '' } } };
      },
      getMRPRuns: async () => {
        const res = await api<any[]>('/api/records?module=manufacturing&kind=mrp');
        if (!res.success) return res;
        return { success: true, data: { runs: res.data || [], plannedOrders: [] } };
      },
      runMRP: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'mrp', ...data }) }),
      getShopFloorData: async () => ({ success: true, data: { operator: null, activeJobs: [], machineStatus: [], shiftMetrics: { target: 0, actual: 0, efficiency: 0, rejects: 0, downtime_mins: 0 } } }),
      recordOutput: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'shop_output', ...data }) }),
      recordDowntime: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'manufacturing', kind: 'shop_downtime', ...data }) }),
      ...new Proxy({}, { get: () => unavailable })
    },
    hrm: {
      getEmployees: async () => api('/api/records?module=hrm&kind=employee'),
      createEmployee: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'hrm', kind: 'employee', ...data }) }),
      updateEmployee: async (_tid: string, id: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'hrm', kind: 'employee', id, ...data }) }),
      getAttendanceReport: async () => api('/api/records?module=hrm&kind=attendance'),
      getLeaves: async () => api('/api/records?module=hrm&kind=leave'),
      createLeave: async (data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'hrm', kind: 'leave', ...data }) }),
      getPayrolls: async () => api('/api/records?module=hrm&kind=payroll'),
      getTaxDeclarations: async () => api('/api/records?module=hrm&kind=tax_declaration'),
      createTaxDeclaration: async (_tid: string, data: any) =>
        api('/api/records', { method: 'POST', body: JSON.stringify({ module: 'hrm', kind: 'tax_declaration', ...data }) }),
      ...new Proxy({}, { get: () => unavailable })
    },
    approvals: {
      getPendingApprovals: async () => {
        const res = await api<any[]>('/api/approvals');
        if (!res.success) return res;
        return { success: true, data: (res.data || []).filter((row) => row.status === 'pending') };
      },
      getApprovalHistory: async () => {
        const res = await api<any[]>('/api/approvals');
        if (!res.success) return res;
        return { success: true, data: (res.data || []).filter((row) => row.status !== 'pending') };
      },
      getAllApprovals: async () => api('/api/approvals'),
      approveRequest: async (_tid: string, requestId: string, approverName: string, comments: string) =>
        api(`/api/approvals/${requestId}/approve`, { method: 'POST', body: JSON.stringify({ approved_by: approverName, comments }) }),
      rejectRequest: async (_tid: string, requestId: string, approverName: string, comments: string) =>
        api(`/api/approvals/${requestId}/reject`, { method: 'POST', body: JSON.stringify({ reason: comments, approved_by: approverName }) }),
      getPendingCount: async () => {
        const res = await api<any[]>('/api/approvals');
        if (!res.success) return res;
        return { success: true, data: { count: (res.data || []).filter((row) => row.status === 'pending').length } };
      },
      ...new Proxy({}, { get: () => unavailable })
    },
    notifications: {
      getActivityFeed: async () => {
        const res = await api<any[]>('/api/dashboard/recent');
        if (!res.success) return res;
        const data = (res.data || []).map((tx) => {
          const type = String(tx.type || '');
          const moduleName = /purchase|grn|payment/i.test(type) ? 'purchase' : /sales|receipt/i.test(type) ? 'sales' : 'finance';
          const tone = /reject|fail/i.test(String(tx.status)) ? 'error' : /pending|draft/i.test(String(tx.status)) ? 'warning' : 'success';
          return {
            id: String(tx.id),
            type: tone,
            module: moduleName,
            title: type || 'Transaction',
            description: [tx.number, tx.party, tx.amount != null ? `₹${tx.amount}` : ''].filter(Boolean).join(' · '),
            timestamp: tx.date ? new Date(tx.date).toISOString() : new Date().toISOString(),
            actor: tx.party || 'System',
            read: false,
          };
        });
        return { success: true, data };
      },
      ...new Proxy({}, { get: () => unavailable })
    },
    master: {
      getTenant: async () => {
        const res = await api<any>('/api/settings/company');
        if (!res.success) return res;
        const company = res.data?.company || {};
        return {
          success: true,
          data: {
            id: company.id || '1',
            name: company.name || 'Oximax agritech Pvt Ltd',
            code: company.code || 'DAIRY',
            ...company,
          },
        };
      },
      updateTenant: async (_tenantId: string, data: any) =>
        api('/api/settings/company', { method: 'PUT', body: JSON.stringify({ company: data }) }),
      getUsers: async () => api('/api/admin/users'),
      createUser: async () => ({
        success: false,
        error: 'New users are set up on the plant server. This screen cannot create a login.',
      }),
      updateUser: async () => ({
        success: false,
        error: 'User changes are made on the plant server. This screen cannot update a login.',
      }),
      getRoles: async () => ({ success: true, data: [] }),
      getUnitsOfMeasure: async () => api('/api/inventory/uom'),
      getHSNCodes: async () => ({ success: true, data: [] }),
      getCategories: async () => ({ success: true, data: [] }),
      getMovementTypes: async (filters?: any) => movementTypeMockApi.getAll(filters),
      getMovementTypeByCode: async (code: string) => movementTypeMockApi.getByCode(code),
      createMovementType: async (data: any) => movementTypeMockApi.create(data),
      updateMovementType: async (id: string, data: any) => movementTypeMockApi.update(id, data),
      deactivateMovementType: async (id: string) => movementTypeMockApi.deactivate(id),
      createCategory: async () => ({
        success: false,
        error: 'Categories are stored on the material. Nothing was saved here.',
      }),
      getCustomers: async () => api('/api/sales/customers'),
      getVendors: async () => purchaseMasterMockApi.getVendors(),
    },
    dairy: dairyMockApi,
    report: new Proxy({}, { get: () => unavailable }),
    platform: 'browser',
    versions: { node: 'N/A', chrome: 'N/A', electron: 'N/A' }
  };
}

{
  const mfg = window.electronAPI?.manufacturing as any;
  if (mfg) {
    mfg.getRawMaterialInspections = async (filters?: { from?: string; to?: string; shifts?: string }) =>
      api(`/api/qc/tanker-reports${qs(filters || {})}`);
    mfg.createRawMaterialInspection = async (data: any) =>
      api('/api/qc/tanker-reports', { method: 'POST', body: JSON.stringify(data) });
    mfg.updateRawMaterialInspection = async (id: string, data: any) =>
      api(`/api/qc/tanker-reports/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    mfg.getProductInspections = async (filters?: { from?: string; to?: string; shifts?: string }) =>
      api(`/api/qc/product-reports${qs(filters || {})}`);
    mfg.createProductInspection = async (data: any) =>
      api('/api/qc/product-reports', { method: 'POST', body: JSON.stringify(data) });
    mfg.updateProductInspection = async (id: string, data: any) =>
      api(`/api/qc/product-reports/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    mfg.getRecoveryLatest = () => api('/api/recovery/latest');
    mfg.getRecoveryByDate = (date: string) => api(`/api/recovery/reports/${encodeURIComponent(date)}`);
    mfg.saveRecovery = (data: any) =>
      api('/api/recovery/reports', { method: 'POST', body: JSON.stringify(data) });
    mfg.getRecoverySchema = () => api('/api/recovery/schema');
    mfg.saveRecoverySchema = (data: any) =>
      api('/api/recovery/schema', { method: 'PUT', body: JSON.stringify(data) });
    mfg.getRecoverySources = (date: string) => api(`/api/recovery/sources${qs({ date })}`);
    mfg.getRecoveryPreviousClosing = (date: string) => api(`/api/recovery/previous-closing${qs({ date })}`);
    mfg.getRecoveryRange = (from: string, to: string) => api(`/api/recovery/reports${qs({ from, to })}`);
    mfg.getRecoveryMonthlySummary = (month: string) => api(`/api/recovery/summary-report${qs({ month })}`);
  }
  const gst = window.electronAPI?.gst as any;
  if (gst) {
    gst.getEWayBills = async () => {
      const res = await api<any[]>('/api/gst/eway-bills');
      if (!res.success) return res;
      return { success: true, data: { bills: res.data || [], consolidated: [] } };
    };
    gst.generateEWayBill = async (_tid: string, data: any) =>
      api('/api/gst/eway-bills', { method: 'POST', body: JSON.stringify(data) });
    gst.updateEWayBill = async (_tid: string, id: string, data: any) =>
      api(`/api/gst/eway-bills/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    gst.cancelEWayBill = async (_tid: string, id: string) =>
      api(`/api/gst/eway-bills/${id}/cancel`, { method: 'POST' });
    gst.extendEWayBill = async () => ({
      success: false,
      error: 'Validity is set on the GST portal after you upload this JSON. This plant copy does not extend a government e-Way Bill.',
    });
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
